import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Button, Card, InputSelect } from '@features/ui';
import { ExportIcon } from '@features/ui/icons';
import { downloadFile } from '@lib/utils';
import { exportStaffFreebie, getStaffFreebie } from '@src/services/reports';
import { TStaffFreebieRes } from '@src/types';

type TChartItem = {
  staff_name: string;
  resets: number;
  new_accounts: number;
  refunds: number;
};

export const StaffFreebie = () => {
  const [selectedFrom, setSelectedFrom] = useState<string>('');
  const [selectedTo, setSelectedTo] = useState<string>('');
  const [reportData, setReportData] = useState<TStaffFreebieRes | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const loadMonthOptions = async () => {
    setIsLoading(true);
    try {
      const response = await getStaffFreebie({});

      setReportData(response);

      if (!selectedFrom && response.month_options_from.length > 0) {
        setSelectedFrom(response.month_options_from[0].value);
      }
      if (!selectedTo && response.month_options_to.length > 0) {
        setSelectedTo(response.month_options_to[0].value);
      }
    } catch (error) {
      console.error('Error loading staff freebie month options:', error);
      toast.error('Failed to load month options.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadMonthOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGetData = async () => {
    if (!selectedFrom || !selectedTo) {
      toast.error('Please select both From and To months.');

      return;
    }

    setIsLoading(true);
    try {
      const response = await getStaffFreebie({
        from_date: selectedFrom,
        to_date: selectedTo,
      });

      setReportData(response);
      toast.success('Report generated successfully.');
    } catch (error) {
      console.error('Error fetching staff freebie report:', error);
      toast.error('Failed to fetch report data.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = async () => {
    if (!selectedFrom || !selectedTo) {
      toast.error('Please select both From and To months.');

      return;
    }

    setIsExporting(true);
    try {
      const response = await exportStaffFreebie({
        from_date: selectedFrom,
        to_date: selectedTo,
      });

      downloadFile(response.data, 'staff-freebie-report');
      toast.success('Report exported successfully.');
    } catch (error) {
      console.error('Error exporting staff freebie report:', error);
      toast.error('Failed to export report.');
    } finally {
      setIsExporting(false);
    }
  };

  const chartData = useMemo<TChartItem[]>(() => {
    if (!reportData) {
      return [];
    }

    return reportData.chart.labels.map((label, index) => ({
      staff_name: label,
      resets: reportData.chart.resets[index] ?? 0,
      new_accounts: reportData.chart.new_accounts[index] ?? 0,
      refunds: reportData.chart.refunds[index] ?? 0,
    }));
  }, [reportData]);

  const totalAccounts = useMemo(() => {
    if (!reportData) {
      return 0;
    }

    return reportData.staffs.reduce(
      (acc, staff) => acc + staff.resets + staff.new_accounts + staff.refunds,
      0
    );
  }, [reportData]);

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="space-y-2">
          <h1 className="text-[23px]">
            Stats on Free accounts and resets given away by staff
          </h1>
          <p className="text-sm text-muted-foreground">
            {totalAccounts > 0
              ? `Found ${totalAccounts} staff freebie events for the selected period.`
              : 'Select months and click GET DATA.'}
          </p>
        </div>

        <Button
          className={'h-9.5 border-1 border-[#C6C6C6] rounded-[3px] w-fit'}
          disabled={isExporting || isLoading || !selectedFrom || !selectedTo}
          variant={'outline'}
          onClick={handleExport}
        >
          <p>{isExporting ? 'Exporting...' : 'Export'}</p>
          <ExportIcon />
        </Button>
      </div>

      <Card>
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[180px]">
            <p className="text-sm font-medium mb-1">From</p>
            <InputSelect
              options={reportData?.month_options_from ?? []}
              value={selectedFrom}
              onChange={setSelectedFrom}
            />
          </div>
          <div className="min-w-[180px]">
            <p className="text-sm font-medium mb-1">To</p>
            <InputSelect
              options={reportData?.month_options_to ?? []}
              value={selectedTo}
              onChange={setSelectedTo}
            />
          </div>
          <Button
            className={'h-9.5 border-1 border-[#C6C6C6] rounded-[3px]'}
            disabled={isLoading}
            variant={'outline'}
            onClick={handleGetData}
          >
            {isLoading ? 'Loading...' : 'GET DATA'}
          </Button>
        </div>
      </Card>

      <Card>
        {chartData.length === 0 ? (
          <div className="py-12 text-center text-sm text-muted-foreground">
            No chart data available for the selected period.
          </div>
        ) : (
          <ResponsiveContainer height={420} width="100%">
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 20, left: 0, bottom: 70 }}
            >
              <CartesianGrid stroke="#E2E8F0" vertical={false} />
              <XAxis
                angle={-30}
                dataKey="staff_name"
                height={90}
                interval={0}
                textAnchor="end"
              />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Bar
                dataKey="resets"
                fill="#A5F64E"
                name="Resets"
                stackId="freebies"
              />
              <Bar
                dataKey="new_accounts"
                fill="#4E9DF6"
                name="New Accounts"
                stackId="freebies"
              />
              <Bar
                dataKey="refunds"
                fill="#F9624E"
                name="Refunds"
                stackId="freebies"
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>

      <div className="space-y-4">
        {(reportData?.staffs ?? []).length === 0 ? (
          <Card>
            <div className="py-10 text-center text-sm text-muted-foreground">
              Run the report to see staff-level details.
            </div>
          </Card>
        ) : (
          reportData?.staffs.map((staff) => (
            <Card key={staff.staff_user_id}>
              <div className="mb-4">
                <h2 className="text-lg font-semibold">{staff.staff_name}</h2>
                <p className="text-sm text-muted-foreground">
                  Resets: {staff.resets} | New Accounts: {staff.new_accounts} |
                  Refunds: {staff.refunds}
                </p>
              </div>

              <div className="overflow-auto">
                <table className="w-full min-w-[980px] text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2 pr-3">User</th>
                      <th className="text-left py-2 pr-3">Resets</th>
                      <th className="text-left py-2 pr-3">Reset Reasons</th>
                      <th className="text-left py-2 pr-3">New Accounts</th>
                      <th className="text-left py-2 pr-3">
                        New Account Reasons
                      </th>
                      <th className="text-left py-2 pr-3">Refunds</th>
                      <th className="text-left py-2">Refund Reasons</th>
                    </tr>
                  </thead>
                  <tbody>
                    {staff.users.map((user) => (
                      <tr
                        key={`${staff.staff_user_id}-${user.leeloo_user_id}`}
                        className="border-b border-border/60 align-top"
                      >
                        <td className="py-3 pr-3">
                          <a
                            className="text-primary underline"
                            href={`/admin/users/${user.leeloo_user_id}`}
                            rel="noreferrer"
                            target="_blank"
                          >
                            {user.display_name}
                          </a>
                        </td>
                        <td className="py-3 pr-3">{user.resets}</td>
                        <td className="py-3 pr-3">
                          {user.reset_reasons.length === 0
                            ? '-'
                            : user.reset_reasons.map((reason, index) => (
                                <p key={index} className="text-xs leading-5">
                                  {index + 1}. {reason}
                                </p>
                              ))}
                        </td>
                        <td className="py-3 pr-3">{user.new_accounts}</td>
                        <td className="py-3 pr-3">
                          {user.new_account_reasons.length === 0
                            ? '-'
                            : user.new_account_reasons.map((reason, index) => (
                                <p key={index} className="text-xs leading-5">
                                  {index + 1}. {reason}
                                </p>
                              ))}
                        </td>
                        <td className="py-3 pr-3">{user.refunds}</td>
                        <td className="py-3">
                          {user.refund_reasons.length === 0
                            ? '-'
                            : user.refund_reasons.map((reason, index) => (
                                <p key={index} className="text-xs leading-5">
                                  {index + 1}. {reason}
                                </p>
                              ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};
